import { Controller } from "@hotwired/stimulus"

const PRIMARY_REFRESH_EVENT = "rafa:primary-refresh-requested"
const MAX_PULL_DISTANCE = 96
const REFRESH_HOLD_DISTANCE = 48
const DRAG_RESISTANCE = 0.45
const DIRECTION_LOCK_DISTANCE = 6

// Adds a native-feeling refresh gesture to the Lesson Library without making
// pull-to-refresh a global page behaviour. Re-selecting the active Lessons tab
// uses the same refresh path, which also provides a keyboard/desktop equivalent.
export default class extends Controller {
  static targets = ["content", "indicator", "status"]
  static values = { threshold: { type: Number, default: 64 } }

  connect() {
    this.boundTouchStart = this.handleTouchStart.bind(this)
    this.boundTouchMove = this.handleTouchMove.bind(this)
    this.boundTouchEnd = this.handleTouchEnd.bind(this)
    this.boundRefreshRequest = this.handleRefreshRequest.bind(this)
    this.boundBeforeCache = this.reset.bind(this)

    this.element.addEventListener("touchstart", this.boundTouchStart, { passive: true })
    this.element.addEventListener("touchmove", this.boundTouchMove, { passive: false })
    this.element.addEventListener("touchend", this.boundTouchEnd, { passive: true })
    this.element.addEventListener("touchcancel", this.boundTouchEnd, { passive: true })
    document.addEventListener(PRIMARY_REFRESH_EVENT, this.boundRefreshRequest)
    document.addEventListener("turbo:before-cache", this.boundBeforeCache)
  }

  disconnect() {
    this.element.removeEventListener("touchstart", this.boundTouchStart)
    this.element.removeEventListener("touchmove", this.boundTouchMove)
    this.element.removeEventListener("touchend", this.boundTouchEnd)
    this.element.removeEventListener("touchcancel", this.boundTouchEnd)
    document.removeEventListener(PRIMARY_REFRESH_EVENT, this.boundRefreshRequest)
    document.removeEventListener("turbo:before-cache", this.boundBeforeCache)
    if (this.fetchFrame) cancelAnimationFrame(this.fetchFrame)
    this.abortController?.abort()
    if (this.failureTimer) clearTimeout(this.failureTimer)
    if (this.refreshTimer) clearTimeout(this.refreshTimer)
    if (this.successTimer) clearTimeout(this.successTimer)
  }

  handleTouchStart(event) {
    if (!this.pullEnabled() || this.refreshing || this.gestureBlocked(event.target) ||
        event.touches.length !== 1 || window.scrollY > 0) return

    const touch = event.touches[0]
    this.dragging = true
    this.verticalGesture = null
    this.startX = touch.clientX
    this.startY = touch.clientY
    this.pullDistance = 0
  }

  handleTouchMove(event) {
    if (!this.dragging || event.touches.length !== 1) return
    if (window.scrollY > 0) {
      this.finishDrag()
      return
    }

    const touch = event.touches[0]
    const deltaX = touch.clientX - this.startX
    const deltaY = touch.clientY - this.startY

    if (this.verticalGesture === null) {
      if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < DIRECTION_LOCK_DISTANCE) return

      this.verticalGesture = deltaY > 0 && Math.abs(deltaY) > Math.abs(deltaX)
      if (!this.verticalGesture) {
        this.finishDrag()
        return
      }
    }

    if (deltaY <= 0) {
      this.renderPull(0)
      return
    }

    event.preventDefault()
    this.renderPull(Math.min(MAX_PULL_DISTANCE, deltaY * DRAG_RESISTANCE))
  }

  handleTouchEnd() {
    if (!this.dragging) return

    const shouldRefresh = this.verticalGesture && this.pullDistance >= this.thresholdValue
    this.dragging = false
    this.verticalGesture = null

    if (shouldRefresh) {
      this.refresh("pull")
    } else {
      this.settle()
    }
  }

  handleRefreshRequest(event) {
    event.preventDefault()
    this.refresh(event.detail?.source || "primary-navigation")
  }

  handleFetchError() {
    if (!this.refreshing) return

    this.refreshing = false
    this.abortController = null
    if (this.refreshTimer) clearTimeout(this.refreshTimer)
    this.refreshTimer = null
    this.element.classList.remove("is-refreshing", "is-armed")
    this.element.classList.add("is-refresh-error")
    this.statusTarget.textContent = "Couldn’t refresh lessons. Try again."
    this.setDistance(REFRESH_HOLD_DISTANCE)
    this.failureTimer = window.setTimeout(() => this.reset(), 1800)
  }

  refresh(source) {
    if (this.refreshing) return

    this.refreshing = true
    this.dragging = false
    this.verticalGesture = null
    this.element.classList.remove("is-pulling", "is-armed", "is-refresh-error")
    this.element.classList.add("is-refreshing")
    this.element.dataset.refreshSource = source
    this.statusTarget.textContent = "Refreshing lessons…"
    this.setDistance(REFRESH_HOLD_DISTANCE)

    // Fetch just the current feed and replace its results. A Turbo page refresh
    // also displays Turbo's blue progress bar and runs its cache lifecycle at
    // request start, which clears this indicator before the response arrives.
    // Keeping the refresh local gives the gesture one continuous loading state.
    this.fetchFrame = requestAnimationFrame(() => {
      this.fetchFrame = null
      if (this.refreshing && this.element.isConnected) this.fetchResults()
    })

    this.refreshTimer = window.setTimeout(() => this.abortController?.abort(), 15000)
  }

  async fetchResults() {
    this.abortController = new AbortController()

    try {
      const response = await window.fetch(document.baseURI, {
        cache: "no-store",
        credentials: "same-origin",
        headers: {
          "Accept": "text/html",
          "X-Rafa-Refresh": "lessons"
        },
        signal: this.abortController.signal
      })

      if (response.redirected && new URL(response.url).pathname !== window.location.pathname) {
        window.location.assign(response.url)
        return
      }
      if (!response.ok) throw new Error(`Lesson refresh failed with ${response.status}`)

      const html = await response.text()
      const freshDocument = new DOMParser().parseFromString(html, "text/html")
      const currentResults = document.getElementById("search_results")
      const freshResults = freshDocument.getElementById("search_results")
      if (!currentResults || !freshResults) throw new Error("Lesson refresh response did not include results")

      currentResults.replaceWith(freshResults)
      this.completeRefresh()
    } catch (error) {
      if (!this.refreshing) return
      this.handleFetchError()
    }
  }

  completeRefresh() {
    this.refreshing = false
    this.abortController = null
    if (this.refreshTimer) clearTimeout(this.refreshTimer)
    this.refreshTimer = null
    this.element.classList.remove("is-refreshing", "is-armed")
    delete this.element.dataset.refreshSource
    this.statusTarget.textContent = "Lessons refreshed."
    this.setDistance(0)
    this.successTimer = window.setTimeout(() => {
      this.statusTarget.textContent = ""
      this.successTimer = null
    }, 1000)
  }

  renderPull(distance) {
    this.pullDistance = distance
    this.element.classList.toggle("is-pulling", distance > 0)
    this.element.classList.toggle("is-armed", distance >= this.thresholdValue)
    this.statusTarget.textContent = distance >= this.thresholdValue ? "Release to refresh lessons" : "Pull to refresh lessons"
    this.setDistance(distance)
  }

  settle() {
    this.element.classList.remove("is-pulling", "is-armed")
    this.statusTarget.textContent = ""
    this.setDistance(0)
    this.pullDistance = 0
  }

  finishDrag() {
    this.dragging = false
    this.verticalGesture = null
    this.settle()
  }

  reset() {
    const abortController = this.abortController
    this.abortController = null
    if (this.fetchFrame) cancelAnimationFrame(this.fetchFrame)
    this.fetchFrame = null
    this.refreshing = false
    this.dragging = false
    this.verticalGesture = null
    this.pullDistance = 0
    this.element.classList.remove("is-pulling", "is-armed", "is-refreshing", "is-refresh-error")
    delete this.element.dataset.refreshSource
    this.statusTarget.textContent = ""
    this.setDistance(0)
    abortController?.abort()
    if (this.failureTimer) clearTimeout(this.failureTimer)
    if (this.refreshTimer) clearTimeout(this.refreshTimer)
    if (this.successTimer) clearTimeout(this.successTimer)
    this.failureTimer = null
    this.refreshTimer = null
    this.successTimer = null
  }

  setDistance(distance) {
    this.element.style.setProperty("--pull-to-refresh-distance", `${Math.round(distance)}px`)
  }

  pullEnabled() {
    return window.matchMedia("(max-width: 767px)").matches
  }

  gestureBlocked(target) {
    return Boolean(target.closest?.(".search-modal.is-open, dialog[open]"))
  }
}
